import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Warehouse as WhIcon, Plus, Trash2, Pencil, ArrowRightLeft, Package } from "lucide-react";

export default function Warehouses() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [stockView, setStockView] = useState([]);
  const [selWh, setSelWh] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", location: "", notes: "", active: true });
  // Transfer
  const [xferOpen, setXferOpen] = useState(false);
  const [xfer, setXfer] = useState({ from_warehouse_id: "", to_warehouse_id: "", notes: "", lines: [] });
  const [xferBusy, setXferBusy] = useState(false);
  const [costCenters, setCostCenters] = useState([]);

  const load = async () => {
    try {
      const [w, p, s, cc] = await Promise.all([
        api.get("/warehouses"),
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/reports/stock-by-warehouse").catch(() => ({ data: { rows: [] } })),
        api.get("/cost-centers").catch(() => ({ data: [] })),
      ]);
      setItems(w.data); setProducts(p.data); setStockView(s.data.rows || []); setCostCenters(cc.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const branchOf = (whId) => costCenters.find(c => c.warehouse_id === whId)?.name || "";
  const whLabel = (w) => { const b = branchOf(w.id); return b ? `${w.name} — فرع: ${b}` : w.name; };

  const openNew = () => { setEditing(null); setForm({ name: "", code: "", location: "", notes: "", active: true }); setOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ name: w.name, code: w.code || "", location: w.location || "", notes: w.notes || "", active: w.active !== false }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    try {
      if (editing) await api.put(`/warehouses/${editing.id}`, form);
      else await api.post("/warehouses", form);
      toast.success("تم الحفظ"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (w) => {
    if (!confirm(`حذف المستودع "${w.name}"?`)) return;
    try { await api.delete(`/warehouses/${w.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openTransfer = () => {
    setXfer({ from_warehouse_id: "", to_warehouse_id: "", notes: "", lines: [{ product_id: "", quantity: 1 }] });
    setXferOpen(true);
  };
  const addXferLine = () => setXfer(x => ({ ...x, lines: [...x.lines, { product_id: "", quantity: 1 }] }));
  const removeXferLine = (i) => setXfer(x => ({ ...x, lines: x.lines.filter((_, idx) => idx !== i) }));
  const doTransfer = async () => {
    if (!xfer.from_warehouse_id || !xfer.to_warehouse_id) return toast.error("اختر المستودعين");
    if (xfer.from_warehouse_id === xfer.to_warehouse_id) return toast.error("المستودعان متطابقان");
    const lines = xfer.lines.filter(l => l.product_id && +l.quantity > 0);
    if (!lines.length) return toast.error("أضف بنداً واحداً على الأقل");
    setXferBusy(true);
    try {
      const { data } = await api.post("/stock-transfer", { ...xfer, lines: lines.map(l => ({ product_id: l.product_id, quantity: +l.quantity })) });
      toast.success(`تم تحويل ${data.lines} صنف من ${data.from} إلى ${data.to}`);
      setXferOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setXferBusy(false); }
  };

  const filteredStock = selWh ? stockView.filter(r => r.warehouse_id === selWh) : stockView;

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <WhIcon className="w-6 h-6 text-blue-600" /> المستودعات
          </h1>
          <p className="text-sm text-muted-foreground mt-1">إدارة عدة مستودعات + تحويل بضاعة + جرد لكل مستودع</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openTransfer} disabled={items.length < 2} className="gap-1 border-blue-300 text-blue-700" data-testid="transfer-btn">
            <ArrowRightLeft className="w-4 h-4" /> تحويل بضاعة
          </Button>
          <Button onClick={openNew} className="gap-1" data-testid="add-warehouse-btn"><Plus className="w-4 h-4" /> مستودع جديد</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-start">الاسم</TableHead>
              <TableHead className="text-start">الفرع المربوط</TableHead>
              <TableHead className="text-start">الرمز</TableHead>
              <TableHead className="text-start">الموقع</TableHead>
              <TableHead className="text-start">الحالة</TableHead>
              <TableHead className="text-end">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">لا توجد مستودعات — أنشئ أول مستودع للبدء</TableCell></TableRow>}
            {items.map(w => (
              <TableRow key={w.id} data-testid={`wh-row-${w.id}`}>
                <TableCell className="font-semibold flex items-center gap-2"><WhIcon className="w-4 h-4 text-blue-500" />{w.name}</TableCell>
                <TableCell className="text-xs">{branchOf(w.id) ? <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{branchOf(w.id)}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-xs font-mono">{w.code || "—"}</TableCell>
                <TableCell className="text-xs">{w.location || "—"}</TableCell>
                <TableCell>{w.active !== false ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">نشط</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-800">معطل</span>}</TableCell>
                <TableCell className="text-end">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(w)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(w)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Stock by warehouse */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold flex items-center gap-2"><Package className="w-4 h-4 text-emerald-500" /> جرد المستودعات</h2>
          <Select value={selWh || "__all__"} onValueChange={(v) => setSelWh(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="wh-filter"><SelectValue placeholder="كل المستودعات" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">كل المستودعات</SelectItem>
              {items.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {filteredStock.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 text-sm">لا توجد حركة مخزون بعد — الفواتير الجديدة ستُسجَّل تلقائياً</div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead className="text-start">الصنف</TableHead><TableHead className="text-start">المستودع</TableHead><TableHead className="text-end">الكمية</TableHead></TableRow></TableHeader>
            <TableBody>
              {filteredStock.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell className="text-xs text-blue-700">{r.warehouse_name}</TableCell>
                  <TableCell className={`text-end tabular-nums font-bold ${r.quantity < 0 ? "text-red-600" : ""}`}>{r.quantity.toLocaleString("ar-EG")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="wh-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل مستودع" : "مستودع جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>الاسم</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wh-name" placeholder="المستودع الرئيسي" autoFocus /></div>
            <div><Label>الرمز</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="wh-code" placeholder="WH-01" /></div>
            <div><Label>الموقع</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="wh-location" placeholder="دمشق - المزة" /></div>
            <div><Label>ملاحظات</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="wh-save-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={xferOpen} onOpenChange={setXferOpen}>
        <DialogContent className="max-w-2xl" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="text-blue-700 flex items-center gap-2"><ArrowRightLeft className="w-5 h-5" /> تحويل بضاعة بين المستودعات / الفروع</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>من مستودع</Label>
                <Select value={xfer.from_warehouse_id || undefined} onValueChange={(v) => setXfer(x => ({ ...x, from_warehouse_id: v }))}>
                  <SelectTrigger data-testid="xfer-from"><SelectValue placeholder="المصدر" /></SelectTrigger>
                  <SelectContent>{items.map(w => <SelectItem key={w.id} value={w.id}>{whLabel(w)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>إلى مستودع</Label>
                <Select value={xfer.to_warehouse_id || undefined} onValueChange={(v) => setXfer(x => ({ ...x, to_warehouse_id: v }))}>
                  <SelectTrigger data-testid="xfer-to"><SelectValue placeholder="الوجهة" /></SelectTrigger>
                  <SelectContent>{items.filter(w => w.id !== xfer.from_warehouse_id).map(w => <SelectItem key={w.id} value={w.id}>{whLabel(w)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5 border border-border rounded-md p-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">بنود التحويل</Label>
                <Button size="sm" variant="outline" onClick={addXferLine} className="h-6 text-[10px]" data-testid="xfer-add-line">+ بند</Button>
              </div>
              {xfer.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_40px] gap-1">
                  <Select value={l.product_id || undefined} onValueChange={(v) => setXfer(x => ({ ...x, lines: x.lines.map((ll, ii) => ii === i ? { ...ll, product_id: v } : ll) }))}>
                    <SelectTrigger className="h-8" data-testid={`xfer-prod-${i}`}><SelectValue placeholder="اختر صنفاً" /></SelectTrigger>
                    <SelectContent>{products.slice(0, 200).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" value={l.quantity} onChange={(e) => setXfer(x => ({ ...x, lines: x.lines.map((ll, ii) => ii === i ? { ...ll, quantity: e.target.value } : ll) }))} className="h-8" data-testid={`xfer-qty-${i}`} />
                  <Button size="sm" variant="ghost" onClick={() => removeXferLine(i)} className="h-8"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                </div>
              ))}
            </div>
            <div><Label>ملاحظات</Label><Input value={xfer.notes} onChange={(e) => setXfer(x => ({ ...x, notes: e.target.value }))} placeholder="سبب التحويل" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setXferOpen(false)}>إلغاء</Button>
            <Button onClick={doTransfer} disabled={xferBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="xfer-confirm-btn">{xferBusy ? "..." : "تنفيذ التحويل"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
